<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ title }}</span>
        </v-card-title>
        <v-card-text class="mt-4">
          <v-form ref="form">
            <v-text-field
              v-model="form.text"
              label="Treść pytania"
              name="url"
              type="text"
              :error-messages="textErrors"
              @input="$v.form.text.$touch()"
              @blur="$v.form.text.$touch()"
            />
            <v-radio-group v-model="form.type" column row>
              <v-radio label="Jednokrotnego wyboru" value="SingleChoiceQuestion" />
              <v-radio label="Wielokrotnego wyboru" value="MultipleChoiceQuestion" />
            </v-radio-group>
            <draggable
              v-model="form.answers"
              :group="{ name: 'answersTree' }"
              class="draggable"
              handle=".question-edit-form__drag-answers"
              @start="drag = true"
              @end="drag = false"
            >
              <v-row v-for="(answer, index) in form.answers" :key="answer.id">
                <v-col cols="9">
                  <v-text-field
                    v-model="form.answers[index].text"
                    :label="`Odpowiedź ${index + 1}`"
                    :name="`answer-text-${index}`"
                    type="text"
                    :error-messages="answerTextErrors[index]"
                    :autofocus="index === form.answers.length - 1"
                    @input="$v.form.answers.$each[index].$touch()"
                    @blur="$v.form.answers.$each[index].$touch()"
                  >
                    <template v-slot:prepend>
                      <v-icon class="question-edit-form__drag-answers">mdi-swap-vertical</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model="form.answers[index].points"
                    label="Ilość punktów"
                    :name="`answer-points-${index}`"
                    type="number"
                    :error-messages="answerPointsErrors[index]"
                    @input="$v.form.answers.$each[index].$touch()"
                    @blur="$v.form.answers.$each[index].$touch()"
                  >
                    <template v-slot:append>
                      <v-icon @click="removeAnswer(index)">mdi-delete</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </draggable>
            <v-divider v-if="form.answers && form.answers.length" class="my-4" />
            <v-text-field
              v-model="newFieldValue"
              label="Nowa odpowiedź"
              name="url-add"
              type="text"
              prepend-icon="mdi-plus-circle"
              :error-messages="answerErrors"
              @click:prepend="addNewField"
              @input="addNewField"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="text--secondary" text @click="close">Anuluj</v-btn>
          <v-btn color="primary" text @click="submit">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, minLength } from 'vuelidate/lib/validators';
import { v4 as uuidv4 } from 'uuid';
import draggable from 'vuedraggable';

export default {
  components: {
    draggable
  },
  mixins: [validationMixin],
  props: {
    show: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    questionToEdit: {
      type: Object,
      default: null
    }
  },
  data: function() {
    return {
      dialog: false,
      loaderDialog: false,
      errors: [],
      form: {
        text: this.questionToEdit ? this.questionToEdit.text : null,
        type: 'SingleChoiceQuestion',
        answers: this.questionToEdit ? JSON.parse(JSON.stringify(this.questionToEdit.answers)) : []
      },
      newFieldValue: ''
    };
  },
  validations: {
    form: {
      text: {
        required
      },
      answers: {
        required,
        minLength: minLength(2),
        $each: {
          text: {
            required
          },
          points: {
            required
          }
        }
      }
    }
  },
  computed: {
    textErrors() {
      const errors = [];
      if (!this.$v.form.text.$dirty) {
        return errors;
      }
      if (!this.$v.form.text.required) {
        errors.push('Treść pytania jest wymagana');
      }
      return errors;
    },
    answerTextErrors() {
      const errors = [];
      for (const i in this.$v.form.answers.$each.$iter) {
        errors[i] = [];
        if (!this.$v.form.answers.$each[i].text.$dirty) {
          continue;
        }
        if (!this.$v.form.answers.$each[i].text.required) {
          errors[i].push('Treść odpowiedzi jest wymagana');
        }
      }
      return errors;
    },
    answerPointsErrors() {
      const errors = [];
      for (const i in this.$v.form.answers.$each.$iter) {
        errors[i] = [];
        if (!this.$v.form.answers.$each[i].points.$dirty) {
          continue;
        }
        if (!this.$v.form.answers.$each[i].points.required) {
          errors[i].push('Ilość punktów jest wymagana');
        }
      }
      return errors;
    },
    answerErrors() {
      const errors = [];
      if (!this.$v.form.answers.$dirty) {
        return errors;
      }
      if (!this.$v.form.answers.minLength || !this.$v.form.answers.required) {
        errors.push('Przynajmniej dwie odpowiedzi są wymagane');
      }
      return errors;
    },
    formValid() {
      return !this.$v.form.$invalid && !this.$v.form.$pending;
    },
    formChanged() {
      return this.$v.$anyDirty;
    }
  },
  watch: {
    show(newVal) {
      this.url = null;
      this.dialog = newVal;
    }
  },
  methods: {
    clear() {
      if (this.questionToEdit) {
        this.form.text = this.questionToEdit.text;
        this.form.answers = JSON.parse(JSON.stringify(this.questionToEdit.answers));
      } else {
        this.form.text = '';
        this.form.answers = [];
      }

      this.newFieldValue = '';
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();

      if (this.formValid) {
        if (this.questionToEdit) {
          this.$emit('updated', {
            question: {
              id: this.questionToEdit.id,
              text: this.form.text,
              answers: this.form.answers,
              type: this.form.type
            }
          });
        } else {
          this.$emit('added', {
            question: {
              id: uuidv4(),
              text: this.form.text,
              answers: this.form.answers,
              type: this.form.type
            }
          });
        }

        this.clear();
        this.$emit('close');
      }
    },
    close() {
      this.clear();
      this.$emit('close');
    },
    async addNewField() {
      const answer = {
        id: uuidv4(),
        text: this.newFieldValue,
        points: 0,
        type: 'SimpleAnswer'
      };
      this.form.answers.push(answer);

      await this.$nextTick();
      this.newFieldValue = '';
    },
    removeAnswer(index) {
      this.form.answers.splice(index, 1);
    }
  }
};
</script>

<style lang="scss">
.question-edit-form__drag-answers {
  cursor: pointer;
}
</style>
